/**
 * Day 5, content understanding, from shallow enrichment onward: the Tier-1
 * enrichment of every post, the Tier-2 representative sample, the
 * Owner-assisted Tier-2 extraction and its review, the Tier-2 feature
 * history, the evaluation scorecard and the manifest.
 */

import type { ReactNode } from 'react';

import { ANALYSIS_STAGES } from '@mouja/domain/analysis';

import {
  continueAnalysisWithShallowEvidence,
  enrichAnalysisContent,
  importDeepAnalysisContent,
  newIdempotencyKey,
  reviewDeepAnalysisContent,
  type AnalysisDay5State,
  type AnalysisRunSummary,
} from '../../api.js';
import { Button, InlineAlert, Tag, TextAreaField } from '../../ui.js';
import { SectionHeading } from '../common.js';
import { parseObjectJson } from './review-json.js';
import { card } from './run-page-styles.js';
import { toneFor } from './run-state-labels.js';
import type { RunMutation } from './useAnalysisRunMutation.js';
import type { ReviewDrafts } from './useReviewDrafts.js';

export function ContentUnderstandingSection({
  id,
  run,
  day5,
  busy,
  mutate,
  drafts,
}: {
  readonly id: string;
  readonly run: AnalysisRunSummary;
  readonly day5: AnalysisDay5State;
  readonly busy: string | null;
  readonly mutate: RunMutation;
  readonly drafts: ReviewDrafts;
}): ReactNode {
  const {
    ownerSelectionJson,
    setOwnerSelectionJson,
    deepAnnotationJson,
    setDeepAnnotationJson,
    deepReviewJson,
    setDeepReviewJson,
  } = drafts;
  if (
    ANALYSIS_STAGES.findIndex((stage) => stage === run.currentStage) <
    ANALYSIS_STAGES.indexOf('SHALLOW_CONTENT_ENRICHMENT')
  ) {
    return null;
  }

  return (
    <section id="content-understanding" style={{ marginBottom: 'var(--space-lg)' }}>
      <SectionHeading>Content understanding</SectionHeading>
      <div style={{ ...card, display: 'grid', gap: 12 }}>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
          Extraction only: these fields describe what appears in the content. They are
          not performance findings or strategic recommendations.
        </p>
        {run.currentStage === 'SHALLOW_CONTENT_ENRICHMENT' &&
        run.executionState === 'PENDING' ? (
          <div style={{ display: 'grid', gap: 10 }}>
            <Button
              variant="primary"
              busy={busy === 'shallow-enrich'}
              onClick={() =>
                void mutate('shallow-enrich', () =>
                  enrichAnalysisContent(
                    id,
                    newIdempotencyKey(),
                    ownerSelectionJson.trim()
                      ? parseObjectJson(ownerSelectionJson, 'Owner selection')
                      : {},
                  ),
                )
              }
            >
              Enrich corpus and select representatives
            </Button>
            <details>
              <summary>Owner-selected representative fallback</summary>
              <p style={{ color: 'var(--text-muted)' }}>
                Only needed when one Business lacks a public metric across three posts.
                Supply three distinct high/mid/low sampling roles for that Business.
                These remain sampling labels, not performance claims.
              </p>
              <TextAreaField
                label="Optional Owner selection JSON"
                value={ownerSelectionJson}
                onChange={setOwnerSelectionJson}
                rows={6}
                maxLength={100_000}
              />
            </details>
          </div>
        ) : null}
      </div>

      {day5.features.some((feature) => feature.enrichmentTier === 'SHALLOW') ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>
            Tier 1 — all posts (
            {
              day5.features.filter((feature) => feature.enrichmentTier === 'SHALLOW')
                .length
            }
            )
          </summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {day5.features
              .filter((feature) => feature.enrichmentTier === 'SHALLOW')
              .map((feature) => {
                const payload = feature.payload;
                return (
                  <article key={feature.id} style={card}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Tag tone="info">{feature.subjectRole}</Tag>
                      <Tag tone={toneFor(feature.reviewState)}>
                        {feature.reviewState}
                      </Tag>
                      <Tag tone="info">
                        {typeof payload['languageMode'] === 'string'
                          ? payload['languageMode']
                          : 'UNKNOWN'}
                      </Tag>
                    </div>
                    <strong>{feature.businessName}</strong>
                    <p>
                      <a href={feature.canonicalUrl} target="_blank" rel="noreferrer">
                        Inspect original post
                      </a>
                    </p>
                    <p style={{ whiteSpace: 'pre-wrap' }}>
                      {typeof payload['originalCaption'] === 'string'
                        ? payload['originalCaption']
                        : 'Caption unavailable'}
                    </p>
                    <p style={{ color: 'var(--text-muted)' }}>
                      Extractor {feature.extractorName}/{feature.extractorVersion} ·
                      unknowns{' '}
                      {Array.isArray(payload['unknowns'])
                        ? payload['unknowns'].length
                        : 0}
                    </p>
                  </article>
                );
              })}
          </div>
        </details>
      ) : null}

      {day5.selections.length > 0 ? (
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Tier 2 representative sample ({day5.selections.length})
          </h3>
          {day5.selections.map((selection) => (
            <article key={selection.id} style={card}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <Tag tone="info">{selection.samplingRole}</Tag>
                <Tag tone="info">{selection.selectionMethod}</Tag>
              </div>
              <strong>{selection.businessName}</strong>
              <p>
                <a href={selection.canonicalUrl} target="_blank" rel="noreferrer">
                  Play or inspect selected media
                </a>
              </p>
              <p style={{ color: 'var(--text-muted)' }}>
                {selection.metricName ?? 'Owner-selected'} {selection.metricValue ?? ''}{' '}
                · age {selection.observedAgeHours ?? 'unknown'} hours
                <br />
                {selection.selectionReason}
              </p>
            </article>
          ))}
        </div>
      ) : null}

      {run.currentStage === 'DEEP_CONTENT_UNDERSTANDING' &&
      run.executionState === 'AWAITING_REVIEW' ? (
        <div style={{ ...card, display: 'grid', gap: 10, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)', margin: 0 }}>
            Owner-assisted Tier 2 extraction
          </h3>
          <p style={{ color: 'var(--text-muted)', margin: 0 }}>
            Inspect each selected post, then submit transcript, OCR, controlled semantic
            fields and evidence timestamps through the frozen Tier-2 contract.
            Provenance is recorded as OWNER_ANNOTATED.
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            <p style={{ color: 'var(--text-muted)', margin: 0 }}>
              If compatible video or audio media is unavailable, continue with the
              observed metrics, captions and deterministic Tier-1 evidence. Mouja
              records the missing Tier-2 model as an explicit limitation.
            </p>
            <Button
              variant="primary"
              busy={busy === 'deep-shallow-fallback'}
              onClick={() =>
                void mutate('deep-shallow-fallback', () =>
                  continueAnalysisWithShallowEvidence(id, newIdempotencyKey()),
                )
              }
            >
              Continue with available evidence
            </Button>
          </div>
          <TextAreaField
            label="Tier-2 annotation JSON"
            value={deepAnnotationJson}
            onChange={setDeepAnnotationJson}
            rows={8}
            maxLength={1_800_000}
          />
          <Button
            variant="outline"
            busy={busy === 'deep-import'}
            disabled={!deepAnnotationJson.trim()}
            onClick={() =>
              void mutate('deep-import', () =>
                importDeepAnalysisContent(
                  id,
                  newIdempotencyKey(),
                  parseObjectJson(deepAnnotationJson, 'Tier-2 annotation'),
                ),
              )
            }
          >
            Save proposed Tier-2 versions
          </Button>

          {day5.features.some(
            (feature) =>
              feature.enrichmentTier === 'DEEP' && feature.reviewState === 'PROPOSED',
          ) ? (
            <>
              <h3 style={{ font: 'var(--type-heading-sm)', marginBottom: 0 }}>
                Review, correct, reject or approve
              </h3>
              <p style={{ color: 'var(--text-muted)', margin: 0 }}>
                A correction creates an approved successor while preserving the original
                proposal. Include the field-level evaluation scorecard in the same
                review.
              </p>
              <TextAreaField
                label="Tier-2 review JSON"
                value={deepReviewJson}
                onChange={setDeepReviewJson}
                rows={8}
                maxLength={1_800_000}
              />
              <Button
                variant="primary"
                busy={busy === 'deep-review'}
                disabled={!deepReviewJson.trim()}
                onClick={() =>
                  void mutate('deep-review', () =>
                    reviewDeepAnalysisContent(
                      id,
                      newIdempotencyKey(),
                      parseObjectJson(deepReviewJson, 'Tier-2 review'),
                    ),
                  )
                }
              >
                Record review and finalize when coverage is sufficient
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {day5.features.some((feature) => feature.enrichmentTier === 'DEEP') ? (
        <details style={{ ...card, marginTop: 12 }} open>
          <summary>Tier-2 feature history</summary>
          <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
            {day5.features
              .filter((feature) => feature.enrichmentTier === 'DEEP')
              .map((feature) => (
                <article key={feature.id} style={card}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <Tag tone={toneFor(feature.reviewState)}>{feature.reviewState}</Tag>
                    <Tag tone="info">v{feature.featureVersion}</Tag>
                    <Tag tone="info">{feature.extractorName}</Tag>
                  </div>
                  <strong>{feature.businessName}</strong>
                  <p style={{ color: 'var(--text-muted)' }}>
                    Evidence spans: {feature.evidence.length} · prior version{' '}
                    {feature.priorFeatureVersionId ?? 'none'}
                  </p>
                  <details>
                    <summary>Structured feature payload</summary>
                    <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {JSON.stringify(feature.payload, null, 2)}
                    </pre>
                  </details>
                </article>
              ))}
          </div>
        </details>
      ) : null}

      {day5.evaluations.length > 0 ? (
        <div style={{ ...card, marginTop: 12 }}>
          <h3 style={{ font: 'var(--type-heading-sm)' }}>
            Evaluation scorecard ({day5.evaluations.length})
          </h3>
          <ul>
            {day5.evaluations.map((evaluation) => (
              <li key={evaluation.id}>
                <b>{evaluation.fieldName}</b> — {evaluation.outcome}
                {evaluation.note ? `: ${evaluation.note}` : ''}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {day5.understandingManifest ? (
        <InlineAlert
          tone="success"
          title="Content-understanding manifest is complete. Audience-signal extraction remains pending."
        />
      ) : null}
    </section>
  );
}
